import TextField from '@mui/material/TextField';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import DropdownAutocomplete from '../FormFields/DropdownAutocomplete';
import {
  ActionProps,
  CombinatorSelectorProps,
  Field,
  FieldSelectorProps,
  formatQuery,
  parseJsonLogic,
  QueryBuilder,
  RuleGroupType,
  ValueEditorProps,
} from 'react-querybuilder';
import React from 'react';
import 'react-querybuilder/dist/query-builder.css';
import MultiSelectAutocomplete from '../FormFields/MultiSelectAutocomplete';
import RemoveAction from './RemoveAction';

type ZuulQueryBuilderProps = {
  query: string;
  setQuery: (query: string) => void;
  fields: Field[];
  isSummary?: boolean;
};

const ZuulQueryBuilder: React.FC<ZuulQueryBuilderProps> = ({ query, setQuery, fields, isSummary = false }) => {
  const parsedQuery = query ? parseJsonLogic(query) : undefined;

  return (
    <div className={'zuul-query-builder'}>
      <QueryBuilder
        showCombinatorsBetweenRules
        autoSelectField={false}
        autoSelectOperator={false}
        defaultQuery={parsedQuery}
        fields={fields}
        onQueryChange={(changedQuery) => {
          const formattedQuery = JSON.stringify(formatQuery(changedQuery, 'jsonlogic'));
          if (formattedQuery !== query) {
            setQuery(formattedQuery);
          }
        }}
        controlElements={{
          valueEditor: CustomValueEditor,
          fieldSelector: CustomSelector,
          operatorSelector: CustomOperatorSelector,
          combinatorSelector: CustomCombinatorSelector,
          addRuleAction: CustomAddAction,
          addGroupAction: CustomAddAction,
          removeGroupAction: RemoveAction,
          removeRuleAction: RemoveAction,
        }}
        disabled={isSummary}
      />
    </div>
  );
};

const CustomValueEditor: React.FC<ValueEditorProps> = ({
  value,
  handleOnChange,
  disabled,
  type,
  values: fieldValues,
}) => {
  if (type === 'multiselect' && fieldValues) {
    let values = Array.isArray(value) ? value : value ? [value] : [];
    if (values.length === 1 && values[0].includes(',')) {
      values = values[0].split(',');
    }
    return (
      <MultiSelectAutocomplete
        options={fieldValues}
        dataKey='name'
        textField='label'
        value={values}
        onChange={(value: any[]) => {
          handleOnChange(value.map((v: any) => (typeof v === 'object' ? v.name : v)));
        }}
        disabled={disabled}
        sx={{ width: '100%' }}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <DropdownAutocomplete
        options={[
          { name: 'true', label: 'True' },
          { name: 'false', label: 'False' },
        ]}
        valueField='name'
        textField='label'
        value={value}
        onChange={(selected: any) => handleOnChange(selected.name)}
        disabled={disabled}
        sx={{ width: '100%' }}
      />
    );
  }

  return (
    <TextField
      type='text'
      value={value}
      onChange={(e) => handleOnChange(e.target.value)}
      placeholder={'Enter value...'}
      disabled={disabled}
    />
  );
};

const CustomSelector: React.FC<FieldSelectorProps | CombinatorSelectorProps> = ({
  options,
  value,
  handleOnChange,
  disabled,
}) => {
  return (
    <DropdownAutocomplete
      options={options as Field[]}
      valueField='name'
      textField='label'
      value={value}
      onChange={(selected: any) => handleOnChange(selected.name)}
      placeholder={'Select...'}
      disabled={disabled}
      sx={{ width: '100%', maxWidth: '15em' }}
    />
  );
};

const CustomCombinatorSelector: React.FC<CombinatorSelectorProps> = ({ options, value, handleOnChange, disabled }) => {
  return (
    <DropdownAutocomplete
      options={options as Field[]}
      valueField='name'
      textField='label'
      value={value}
      onChange={(selected: any) => handleOnChange(selected.name)}
      placeholder={'Select...'}
      disabled={disabled}
      sx={{ width: '100%', maxWidth: '7em' }}
    />
  );
};

const CustomOperatorSelector: React.FC<FieldSelectorProps> = ({ options, value, handleOnChange, disabled }) => {
  return (
    <DropdownAutocomplete
      options={options as Field[]}
      valueField='name'
      textField='label'
      value={value}
      onChange={(selected: any) => handleOnChange(selected.name)}
      placeholder={'Select...'}
      disabled={disabled}
      sx={{ width: '100%', maxWidth: '10em' }}
    />
  );
};

const CustomAddAction: React.FC<ActionProps> = ({ label, handleOnClick, disabled }) => {
  if (disabled) {
    return null;
  }

  return (
    <Button color='success' onClick={handleOnClick} startIcon={<Add fontSize='small' />}>
      {label?.replace('+', '')}
    </Button>
  );
};

export default ZuulQueryBuilder;
